import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@/components/Alert';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/States';
import { useAiCredits, useAiUsage } from '@/features/ask-pilot/api';
import { CreditsCard } from '@/features/ask-pilot/CreditsCard';
import { getErrorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

const monthOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const shift = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number);
  return monthOf(new Date(y, m - 1 + by, 1));
};
const monthLabel = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

/** Every question asked and every boost given, one month at a time. */
export function AskPilotUsagePage() {
  const current = monthOf(new Date());
  const [month, setMonth] = useState(current);
  const credits = useAiCredits();
  const usage = useAiUsage(month);
  const rows = usage.data?.rows ?? [];
  const charged = rows.filter((r) => r.kind === 'question' && !r.refunded).length;

  return (
    <>
      <PageHeader
        title="Ask Pilot usage"
        description="Every question asked and every boost added."
        actions={
          <Link to="/ask-pilot" className="btn btn-sm btn-secondary">
            ← Back to Ask Pilot
          </Link>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <section className="card min-w-0">
          <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setMonth(shift(month, -1))} aria-label="Previous month">
              ←
            </button>
            <h2 className="flex-1 text-center text-sm font-semibold text-slate-900">{monthLabel(month)}</h2>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={month >= current}
              onClick={() => setMonth(shift(month, 1))}
              aria-label="Next month"
            >
              →
            </button>
          </div>
          {usage.isLoading ? (
            <div className="space-y-2 p-5">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : usage.isError ? (
            <div className="p-5">
              <Alert tone="error">{getErrorMessage(usage.error, 'Could not load usage.')}</Alert>
            </div>
          ) : rows.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No questions this month.</p>
          ) : (
            <>
              <p className="px-5 pt-3 text-xs text-slate-500">
                {charged} credit{charged === 1 ? '' : 's'} used. Questions the AI could not answer cost nothing.
              </p>
              <ul className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <li key={`${r.at}-${i}`} className="flex items-start gap-3 px-5 py-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-slate-900">{r.kind === 'boost' ? `Boost added${r.text ? ` · ${r.text}` : ''}` : r.text}</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(r.at)}
                        {r.kind === 'question' && r.source === 'BOOST' ? ' · from boost' : ''}
                        {r.refunded ? ' · not answered, refunded' : ''}
                      </p>
                    </div>
                    <span className={`tabular-nums ${r.credits > 0 ? 'text-emerald-700' : r.credits < 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                      {r.credits > 0 ? `+${r.credits}` : r.credits}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
        <aside>{credits.data ? <CreditsCard credits={credits.data} /> : null}</aside>
      </div>
    </>
  );
}
